import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { scanningConfig } from '../../Config';
import { getToken } from '../../helpers/AuthHelpers';
import {
    RawSetting,
    RawSettings,
    UpdateSettingDescriptionRequest,
    UpdateSettingOverridesRequest,
    UpdateSettingValueRequest,
    UserSettings
} from '../../types/UserSettings';

export const SettingsAPI = createApi({
    reducerPath: 'SettingsAPI',
    baseQuery: retry(
        fetchBaseQuery({
            baseUrl: `${scanningConfig.apiPath}/`,
            prepareHeaders: (headers) => {
                const token = getToken();
                if (token) {
                    headers.set('Authorization', `Bearer ${token}`);
                    headers.set('Camel-Case', 'true');
                }
                return headers;
            }
        }),
        { maxRetries: 0 }
    ),
    tagTypes: ['Settings', 'UserSettings', 'RawSettings'],
    endpoints: (builder) => ({
        getSettings: builder.query<UserSettings, void>({
            query: () => ({ url: 'settings', method: 'GET' }),
            providesTags: ['UserSettings']
        }),
        getEvaluatedSettings: builder.query<UserSettings, void>({
            query: () => ({ url: 'settings', method: 'GET', params: { include_metadata: true } }),
            providesTags: ['UserSettings']
        }),
        getRawSettings: builder.query<RawSettings, void>({
            query: () => ({ url: 'settings/raw', method: 'GET' }),
            providesTags: ['RawSettings']
        }),
        updateSettingValue: builder.mutation<RawSetting, { key: string; body: UpdateSettingValueRequest }>({
            query: ({ key, body }) => ({ url: `settings/${key}/value`, method: 'PATCH', body }),
            invalidatesTags: ['RawSettings', 'UserSettings']
        }),
        updateSettingDescription: builder.mutation<RawSetting, { key: string; body: UpdateSettingDescriptionRequest }>({
            query: ({ key, body }) => ({ url: `settings/${key}/description`, method: 'PATCH', body }),
            invalidatesTags: ['RawSettings', 'UserSettings']
        }),
        updateSettingOverrides: builder.mutation<RawSetting, { key: string; body: UpdateSettingOverridesRequest }>({
            query: ({ key, body }) => ({ url: `settings/${key}/overrides`, method: 'PATCH', body }),
            invalidatesTags: ['RawSettings', 'UserSettings']
        })
    })
});

export const {
    useLazyGetSettingsQuery,
    useGetEvaluatedSettingsQuery,
    useGetRawSettingsQuery,
    useUpdateSettingValueMutation,
    useUpdateSettingDescriptionMutation,
    useUpdateSettingOverridesMutation
} = SettingsAPI;
